import React, { useState, useEffect } from 'react';
// Still imported: other pages (OrderView, SalaryGenerator, AddUser, StoreLogin,
// ExchangeBillWholesale) use the same global class names this file defines.
// This page itself is styled by CustomerOrder.module.css.
import './CustomerOrder.css';
import styles from './CustomerOrder.module.css';
import { API_BASE_URL } from '../Config.js';
import {
    CalendarDays,
    ClipboardList,
    IndianRupee,
    Phone,
    Plus,
    Receipt,
    Send,
    Trash2,
    UserRound,
    Wallet,
    X,
} from 'lucide-react';

// Amount with Indian digit grouping, for display only
const formatAmount = (value) =>
    Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CustomerOrder = () => {
    const user = JSON.parse(sessionStorage.getItem('user'));
    const storeId = user?.storeId || '';
    const [order, setOrder] = useState({
        customerName: '',
        mobileNo: '',
        deliveryDate: '',
        totalAmount: 0,
        advancePayment: 0,
        storeId: storeId,
        orders: [{ schoolName: '', itemType: '', description: '', size: '', quantity: 0, price: 0, amount: 0 }]
    });
    const [schools, setSchools] = useState([]);
    const [itemTypes, setItemTypes] = useState([]);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const userData = JSON.parse(sessionStorage.getItem('user'));
        const storeId = userData?.storeId;

        if (storeId) {
            // Fetch school names
            fetch(`${API_BASE_URL}/user/filter/getSchool?storeId=${storeId}`)
                .then((response) => response.json())
                .then((data) => setSchools(data))
                .catch((error) => console.error('Error fetching school names:', error));

            // Fetch item types
            fetch(`${API_BASE_URL}/user/filter/item_type?storeId=${storeId}`)
                .then((response) => response.json())
                .then((data) => setItemTypes(data))
                .catch((error) => console.error('Error fetching item types:', error));
        } else {
            console.error('Store ID not found in session storage');
        }
    }, []);

    // Close the confirm modal on Escape
    useEffect(() => {
        if (!showModal) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setShowModal(false);
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showModal]);

    const handleInputChange = (e, index, field) => {
        const newOrders = [...order.orders];
        let value = e.target.value;

        if ((field === 'price' || field === 'quantity') && value < 0) {
            value = 0;
        }

        newOrders[index][field] = value;

        if (field === 'price' || field === 'quantity') {
            newOrders[index].amount = newOrders[index].price * newOrders[index].quantity;
        }

        const totalAmount = newOrders.reduce((sum, item) => sum + item.amount, 0);

        setOrder({ ...order, orders: newOrders, totalAmount });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setShowModal(true);
    };

    const handleConfirm = () => {
        const user = JSON.parse(sessionStorage.getItem('user'));
        const storeId = user?.storeId; // Retrieve storeId from user data

        const updatedOrder = {
            ...order,
            storeId: storeId // Include storeId in the order object
        };

        fetch(`${API_BASE_URL}/user/customer_order_details`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(order)
        })
            .then(response => {
                if (response.ok) {
                    alert('Order submitted successfully');
                    setOrder({
                        customerName: '',
                        mobileNo: '',
                        deliveryDate: '',
                        totalAmount: 0,
                        advancePayment: 0,
                        orders: [{ schoolName: '', itemType: '', description: '', size: '', quantity: 0, price: 0, amount: 0 }]
                    });
                    setShowModal(false);
                } else {
                    alert('Failed to submit order');
                    setShowModal(false);
                }
            })
            .catch(error => {
                console.error('Error submitting order:', error);
                setShowModal(false);
            });
    };

    const addOrder = () => {
        setOrder({
            ...order,
            orders: [...order.orders, { schoolName: '', itemType: '', description: '', size: '', quantity: 0, price: 0, amount: 0 }]
        });
    };

    const handleDelete = (index) => {
        const newOrders = order.orders.filter((_, i) => i !== index);
        const totalAmount = newOrders.reduce((sum, item) => sum + item.amount, 0);
        setOrder({ ...order, orders: newOrders, totalAmount });
    };

    const itemCount = order.orders.length;
    const totalQuantity = order.orders.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    const amountDue = Number(order.totalAmount) - Number(order.advancePayment);

    const summaryCards = [
        { key: 'total', label: 'Total amount', value: order.totalAmount, icon: IndianRupee, primary: true },
        { key: 'advance', label: 'Advance payment', value: order.advancePayment, icon: Wallet },
        { key: 'due', label: 'Amount due', value: amountDue, icon: Receipt },
    ];

    return (
        <>
            {/* Confirm modal — rendered outside the page so it covers the whole dashboard */}
            {showModal && (
                <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
                    <div
                        className={styles.modal}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="order-summary-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.modalHeader}>
                            <div>
                                <h3 id="order-summary-title" className={styles.modalTitle}>Order summary</h3>
                                <p className={styles.modalSubtitle}>
                                    {[order.customerName, order.mobileNo].filter(Boolean).join(' · ') ||
                                        'Check the amounts before confirming.'}
                                </p>
                            </div>
                            <button
                                type="button"
                                className={styles.iconBtn}
                                onClick={() => setShowModal(false)}
                                aria-label="Close"
                            >
                                <X size={18} aria-hidden="true" />
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <dl className={styles.summaryList}>
                                <div className={styles.summaryRow}>
                                    <dt className={styles.summaryRowLabel}>Items</dt>
                                    <dd className={styles.summaryRowValue}>{itemCount}</dd>
                                </div>
                                <div className={styles.summaryRow}>
                                    <dt className={styles.summaryRowLabel}>Total amount</dt>
                                    <dd className={styles.summaryRowValue}>₹{formatAmount(order.totalAmount)}</dd>
                                </div>
                                <div className={styles.summaryRow}>
                                    <dt className={styles.summaryRowLabel}>Advance payment</dt>
                                    <dd className={styles.summaryRowValue}>₹{formatAmount(order.advancePayment)}</dd>
                                </div>
                                <div className={`${styles.summaryRow} ${styles.summaryRowDue}`}>
                                    <dt className={styles.summaryRowLabel}>Amount due</dt>
                                    <dd className={styles.summaryRowValue}>₹{formatAmount(amountDue)}</dd>
                                </div>
                            </dl>
                        </div>

                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={`${styles.btn} ${styles.btnGhost}`}
                                onClick={() => setShowModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={`${styles.btn} ${styles.btnPrimary}`}
                                onClick={handleConfirm}
                            >
                                Confirm order
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.page}>
                <header className={styles.header}>
                    <div className={styles.titleBlock}>
                        <span className={styles.headerIcon} aria-hidden="true">
                            <ClipboardList size={20} />
                        </span>
                        <h1 className={styles.title}>Customer Order</h1>
                        <div className={styles.chips}>
                            <span className={styles.chip}>
                                {itemCount} {itemCount === 1 ? 'item' : 'items'}
                            </span>
                            {order.deliveryDate && (
                                <span className={styles.chip}>
                                    <CalendarDays size={13} aria-hidden="true" />
                                    Delivery {order.deliveryDate.split('-').reverse().join('-')}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className={styles.headerActions}>
                        <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={addOrder}>
                            <Plus size={15} aria-hidden="true" />
                            Add item
                        </button>
                        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleSubmit}>
                            <Send size={15} aria-hidden="true" />
                            Submit order
                        </button>
                    </div>
                </header>

                {/* -------------------- Summary cards -------------------- */}
                <div className={styles.summaryGrid}>
                    {summaryCards.map(({ key, label, value, icon: Icon, primary }) => (
                        <div key={key} className={`${styles.summaryCard} ${primary ? styles.summaryCardPrimary : ''}`}>
                            <span className={styles.summaryIcon} aria-hidden="true">
                                <Icon size={18} />
                            </span>
                            <div className={styles.summaryText}>
                                <span className={styles.summaryLabel}>{label}</span>
                                <span className={styles.summaryValue}>₹{formatAmount(value)}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* -------------------- Customer details -------------------- */}
                <section className={styles.card} aria-labelledby="order-customer-title">
                    <div className={styles.cardHeader}>
                        <h2 id="order-customer-title" className={styles.cardTitle}>Customer details</h2>
                    </div>
                    <div className={styles.cardBody}>
                        <form onSubmit={handleSubmit} className={styles.fieldGrid}>
                            <div className={styles.field}>
                                <label htmlFor="order-customer-name" className={styles.label}>Customer name</label>
                                <div className={styles.inputWrap}>
                                    <UserRound size={16} className={styles.inputIcon} aria-hidden="true" />
                                    <input
                                        id="order-customer-name"
                                        type="text"
                                        className={styles.input}
                                        value={order.customerName}
                                        onChange={e => setOrder({ ...order, customerName: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className={styles.field}>
                                <label htmlFor="order-mobile" className={styles.label}>Mobile number</label>
                                <div className={styles.inputWrap}>
                                    <Phone size={16} className={styles.inputIcon} aria-hidden="true" />
                                    <input
                                        id="order-mobile"
                                        type="text"
                                        inputMode="tel"
                                        className={styles.input}
                                        value={order.mobileNo}
                                        onChange={e => setOrder({ ...order, mobileNo: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className={styles.field}>
                                <label htmlFor="order-advance" className={styles.label}>Advance payment</label>
                                <div className={styles.inputWrap}>
                                    <Wallet size={16} className={styles.inputIcon} aria-hidden="true" />
                                    <input
                                        id="order-advance"
                                        type="number"
                                        className={styles.input}
                                        value={order.advancePayment}
                                        onChange={e => setOrder({ ...order, advancePayment: Number(e.target.value) })}
                                    />
                                </div>
                            </div>
                            <div className={styles.field}>
                                <label htmlFor="order-delivery" className={styles.label}>Delivery date</label>
                                <div className={styles.inputWrap}>
                                    <CalendarDays size={16} className={styles.inputIcon} aria-hidden="true" />
                                    <input
                                        id="order-delivery"
                                        type="date"
                                        className={styles.input}
                                        value={order.deliveryDate}
                                        onChange={e => setOrder({ ...order, deliveryDate: e.target.value })}
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </section>

                {/* -------------------- Order items -------------------- */}
                <section className={`${styles.card} ${styles.cardFill}`} aria-labelledby="order-items-title">
                    <div className={styles.cardHeader}>
                        <h2 id="order-items-title" className={styles.cardTitle}>
                            Order items
                            <span className={styles.cardHint}>
                                {itemCount} {itemCount === 1 ? 'row' : 'rows'}
                            </span>
                        </h2>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`}
                            onClick={addOrder}
                        >
                            <Plus size={14} aria-hidden="true" />
                            Add item
                        </button>
                    </div>

                    <div className={styles.tableWrap}>
                        {itemCount > 0 ? (
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>School Name</th>
                                        <th>Item Type</th>
                                        <th>Description</th>
                                        <th>Size</th>
                                        <th className={styles.num}>Quantity</th>
                                        <th className={styles.num}>Price</th>
                                        <th className={styles.num}>Amount</th>
                                        <th className={styles.alignCenter}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order.orders.map((orderItem, index) => (
                                        <tr key={index}>
                                            <td className={styles.rowIndex}>{index + 1}</td>
                                            <td>
                                                <select
                                                    className={styles.cellSelect}
                                                    value={orderItem.schoolName}
                                                    onChange={(e) => handleInputChange(e, index, 'schoolName')}
                                                    aria-label={`School for item ${index + 1}`}
                                                >
                                                    <option value="">Select School</option>
                                                    {schools.map((school, idx) => (
                                                        <option key={idx} value={school}>
                                                            {school}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <select
                                                    className={styles.cellSelect}
                                                    value={orderItem.itemType}
                                                    onChange={(e) => handleInputChange(e, index, 'itemType')}
                                                    aria-label={`Item type for item ${index + 1}`}
                                                >
                                                    <option value="">Select Item Type</option>
                                                    {itemTypes.map((item, idx) => (
                                                        <option key={idx} value={item}>
                                                            {item}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className={styles.cellInput}
                                                    placeholder="Description"
                                                    value={orderItem.description}
                                                    onChange={(e) => handleInputChange(e, index, 'description')}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className={styles.cellInput}
                                                    placeholder="Size"
                                                    value={orderItem.size}
                                                    onChange={(e) => handleInputChange(e, index, 'size')}
                                                />
                                            </td>
                                            <td className={styles.num}>
                                                <input
                                                    type="number"
                                                    className={`${styles.cellInput} ${styles.cellInputNum}`}
                                                    placeholder="Quantity"
                                                    value={orderItem.quantity}
                                                    onChange={(e) => handleInputChange(e, index, 'quantity')}
                                                    aria-label={`Quantity for item ${index + 1}`}
                                                />
                                            </td>
                                            <td className={styles.num}>
                                                <input
                                                    type="number"
                                                    className={`${styles.cellInput} ${styles.cellInputNum}`}
                                                    placeholder="Price"
                                                    value={orderItem.price}
                                                    onChange={(e) => handleInputChange(e, index, 'price')}
                                                    aria-label={`Price for item ${index + 1}`}
                                                />
                                            </td>
                                            <td className={`${styles.num} ${styles.amountCell}`}>
                                                {formatAmount(orderItem.amount)}
                                            </td>
                                            <td className={styles.alignCenter}>
                                                <button
                                                    type="button"
                                                    className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                                                    onClick={() => handleDelete(index)}
                                                    aria-label={`Delete item ${index + 1}`}
                                                    title="Delete"
                                                >
                                                    <Trash2 size={16} aria-hidden="true" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="5">Total</td>
                                        <td className={styles.num}>{totalQuantity}</td>
                                        <td />
                                        <td className={styles.num}>{formatAmount(order.totalAmount)}</td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        ) : (
                            <div className={styles.noResults}>
                                <span className={styles.noResultsIcon} aria-hidden="true">
                                    <ClipboardList size={28} />
                                </span>
                                <p className={styles.noResultsTitle}>No items yet</p>
                                <p className={styles.noResultsText}>Add the school, item type, size and quantity for each item.</p>
                                <button
                                    type="button"
                                    className={`${styles.btn} ${styles.btnPrimary} ${styles.noResultsAction}`}
                                    onClick={addOrder}
                                >
                                    <Plus size={15} aria-hidden="true" />
                                    Add item
                                </button>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </>
    );
};

export default CustomerOrder;
